import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {CountdownComponent, CountdownConfig} from 'ngx-countdown';
import {RxwebValidators} from '@rxweb/reactive-form-validators';
import {environment} from '../../../../environments/environment';
import {NewsletterContactApiModel} from '../../../api/models/newsletter-contact.api.model';
import {StaticPageApiModel} from '../../../api/models/static-page.api.model';
import {NewsletterContactRestService} from '../../../api/services/newsletter-contact.rest.service';
import {StaticPageRestService} from '../../../api/services/static-page.rest.service';
import {ResponseErrorsHandlerService} from '../../services/response-errors-handler.service';
import {ResponseNotificationService} from '../../services/response-notification.service';
import {SeoService} from '../../services/seo.service';
import {FeatureFlagService} from '../../feature-flags/feature-flag.service';
import {FeatureFlag} from '../../feature-flags/feature-flag.types';
import {TokenDataProviderService} from '../../services/token-data-provider.service';

interface LandingNavLink {
  labelKey: string;
  route?: string;
  section?: string;
}

interface LandingFeatureItem {
  icon: string;
  titleKey: string;
  descriptionKey: string;
  route?: string;
  section?: string;
}

const CountdownTimeUnits: Array<[string, number]> = [
  ['Y', 1000 * 60 * 60 * 24 * 365],
  ['M', 1000 * 60 * 60 * 24 * 30],
  ['D', 1000 * 60 * 60 * 24],
  ['H', 1000 * 60 * 60],
  ['m', 1000 * 60],
  ['s', 1000],
  ['S', 1],
];

@Component({
  selector: 'app-landing-component',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.scss'],
})
export class LandingComponent implements OnInit, AfterViewInit {
  private readonly registrationLaunchDate = new Date('2026-07-01');

  public readonly logoType = 'assets/layout/images/wtg-logo-transparent-white.webp';
  public readonly footerLogoType = 'assets/layout/images/wtg-logo-transparent-white-sm.webp';
  public readonly heroLayer0 = 'assets/img/landing_layer_1.png';
  public readonly heroLayer1 = 'assets/img/landing_layer_2.png';
  public readonly heroLayer2 = 'assets/img/landing_layer_3.png';
  public readonly heroMobileImage = 'assets/img/landing_layer_1.png';
  public readonly heroMobileMidImage = 'assets/img/landing_layer_2.png';
  public readonly heroMobileFrontImage = 'assets/img/landing_layer_3.png';
  public readonly collaborationImage = 'assets/landing/ultima/collaboration-image.png';
  public readonly eventsImage = 'assets/landing/ultima/easyfollow-image.png';
  public readonly communityShowcaseImage = 'assets/img/project-image1.jpg';
  public readonly visibilityShowcaseImage = 'assets/img/project-image2.jpg';
  public readonly ellipseImage = 'assets/landing/ultima/landing-ellipse.png';
  public readonly ellipseImageSecondary = 'assets/landing/ultima/landing-ellipse2.png';
  public readonly storeBadges = {
    google: 'assets/landing/google-play-badge.svg',
    apple: 'assets/landing/app-store-badge.svg'
  };
  public readonly socialLinks: Array<{icon: string; href: string; label: string}> = [
    {
      icon: 'pi pi-facebook',
      href: 'https://www.facebook.com/WatchThisGarage',
      label: 'Facebook'
    },
    {
      icon: 'pi pi-instagram',
      href: 'https://www.instagram.com/watch.this.garage/',
      label: 'Instagram'
    }
  ];
  public readonly topbarSectionLinks: LandingNavLink[] = [
    {labelKey: 'landing.topbar.home', section: '#header'},
    {labelKey: 'landing.topbar.features', section: '#features'},
    {labelKey: 'landing.topbar.collaboration', section: '#collaboration'},
    {labelKey: 'landing.topbar.events', section: '#events'},
    {labelKey: 'landing.topbar.video', section: '#video'}
  ];
  public readonly topbarRouteLinks: LandingNavLink[] = [
    {labelKey: 'shared.top_bar.menu.feed', route: '/feed'},
    {labelKey: 'shared.top_bar.menu.ranking', route: '/ranking'},
    {labelKey: 'shared.top_bar.menu.events', route: '/events'},
    {labelKey: 'shared.top_bar.menu.marketplace', route: '/marketplace'}
  ];
  public readonly footerExploreLinks: LandingNavLink[] = [
    {labelKey: 'landing.topbar.home', section: '#header'},
    {labelKey: 'landing.topbar.features', section: '#features'},
    {labelKey: 'landing.topbar.collaboration', section: '#collaboration'},
    {labelKey: 'landing.topbar.events', section: '#events'},
    {labelKey: 'landing.topbar.video', section: '#video'}
  ];
  public readonly footerRouteLinks: LandingNavLink[] = [
    {labelKey: 'auth.login.button.sign_in', route: '/login'},
    {labelKey: 'auth.registration.button.register', route: '/registration'},
    {labelKey: 'shared.top_bar.menu.feed', route: '/feed'},
    {labelKey: 'shared.top_bar.menu.events', route: '/events'},
    {labelKey: 'shared.top_bar.menu.ranking', route: '/ranking'},
    {labelKey: 'shared.top_bar.menu.marketplace', route: '/marketplace'},
  ];
  public readonly logoStrip = [
    'assets/landing/ultima/landing-logo1.png',
    'assets/landing/ultima/landing-logo2.png',
    'assets/landing/ultima/landing-logo3.png',
    'assets/landing/ultima/landing-logo4.png'
  ];
  public readonly moduleItems: LandingFeatureItem[] = [
    {
      icon: 'pi pi-bolt',
      titleKey: 'landing.pillars.feed.title',
      descriptionKey: 'landing.pillars.feed.description',
      route: '/feed'
    },
    {
      icon: 'pi pi-chart-line',
      titleKey: 'landing.pillars.ranking.title',
      descriptionKey: 'landing.pillars.ranking.description',
      route: '/ranking'
    },
    {
      icon: 'pi pi-calendar',
      titleKey: 'landing.pillars.events.title',
      descriptionKey: 'landing.pillars.events.description',
      route: '/events'
    },
    {
      icon: 'pi pi-shopping-bag',
      titleKey: 'landing.pillars.marketplace.title',
      descriptionKey: 'landing.pillars.marketplace.description',
      route: '/marketplace'
    }
  ];
  public readonly toolItems: LandingFeatureItem[] = [
    {
      icon: 'pi pi-car',
      titleKey: 'landing.feature_cards.garage.title',
      descriptionKey: 'landing.feature_cards.garage.description',
      route: '/registration'
    },
    {
      icon: 'pi pi-history',
      titleKey: 'landing.feature_cards.history.title',
      descriptionKey: 'landing.feature_cards.history.description',
      route: '/registration'
    },
    {
      icon: 'pi pi-wallet',
      titleKey: 'landing.feature_cards.goals.title',
      descriptionKey: 'landing.feature_cards.goals.description',
      section: '#collaboration'
    },
    {
      icon: 'pi pi-images',
      titleKey: 'landing.feature_cards.media.title',
      descriptionKey: 'landing.feature_cards.media.description',
      section: '#video'
    },
    {
      icon: 'pi pi-map-marker',
      titleKey: 'landing.feature_cards.events.title',
      descriptionKey: 'landing.feature_cards.events.description',
      route: '/events'
    },
    {
      icon: 'pi pi-trophy',
      titleKey: 'landing.feature_cards.ranking.title',
      descriptionKey: 'landing.feature_cards.ranking.description',
      route: '/ranking'
    },
    {
      icon: 'pi pi-shopping-bag',
      titleKey: 'landing.feature_cards.market.title',
      descriptionKey: 'landing.feature_cards.market.description',
      route: '/marketplace'
    },
    {
      icon: 'pi pi-users',
      titleKey: 'landing.feature_cards.collaboration.title',
      descriptionKey: 'landing.feature_cards.collaboration.description',
      section: '#collaboration'
    }
  ];

  public config: CountdownConfig = {};
  @ViewChild('menu') menuViewChild?: ElementRef<HTMLElement>;
  @ViewChild('cd', {static: false}) private countdown?: CountdownComponent;

  public isMenuActive = false;
  public version = environment.version;
  public text: Record<string, string> = {};
  public form!: UntypedFormGroup;
  public loading = false;
  public footerPages: StaticPageApiModel[] = [];
  public shouldShowRegistrationCountdown = false;

  public get shouldShowExtendedLandingContent(): boolean {
    return this.tokenDataProvider.isLoggedIn()
      || this.tokenDataProvider.isLoggedInSSR()
      || this.featureFlags.isEnabled(FeatureFlag.GUEST_LANDING_EXTENDED_CONTENT);
  }

  public get visibleTopbarSectionLinks(): LandingNavLink[] {
    return this.getVisibleSectionLinks(this.topbarSectionLinks);
  }

  public get visibleFooterExploreLinks(): LandingNavLink[] {
    return this.getVisibleSectionLinks(this.footerExploreLinks);
  }

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly translate: TranslateService,
    private readonly fb: UntypedFormBuilder,
    private readonly rns: ResponseNotificationService,
    private readonly reh: ResponseErrorsHandlerService,
    private readonly newsletterContactRS: NewsletterContactRestService,
    private readonly seo: SeoService,
    private readonly staticPageRS: StaticPageRestService,
    private readonly featureFlags: FeatureFlagService,
    private readonly tokenDataProvider: TokenDataProviderService,
  ) {
  }

  ngOnInit(): void {
    this.createForm();
    this.loadFooterPages();
    const description = this.translate.instant('meta.description');
    const resolvedDescription = description && description !== 'meta.description'
      ? description
      : 'Watch This Garage - automotive social app and car market.';
    this.seo.setTitle('WTG', ' | Watch This Garage');
    this.seo.setDescription(resolvedDescription);
    this.seo.setOpenGraph({
      title: 'WTG | Watch This Garage',
      description: resolvedDescription,
      url: this.seo.getAbsoluteUrl('/'),
      type: 'website'
    });
    this.seo.setCanonical('/');
    this.seo.setJsonLd([
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Watch This Garage',
        alternateName: 'WTG',
        url: this.seo.getAbsoluteUrl('/'),
        logo: this.seo.getAbsoluteUrl('/assets/layout/images/wtg-logo-transparent-black.webp')
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Watch This Garage',
        alternateName: 'WTG',
        url: this.seo.getAbsoluteUrl('/')
      }
    ]);
  }

  async ngAfterViewInit(): Promise<void> {
    await this.handleTranslationChange();
    this.initCountdownConfig();
    this.countdown?.begin();
  }

  initCountdownConfig(): void {
    const leftTime = this.getTimestampIntervalDiffFromNow(this.registrationLaunchDate);
    this.shouldShowRegistrationCountdown = leftTime > 0;

    if (!this.shouldShowRegistrationCountdown) {
      this.config = {};
      return;
    }

    this.config = {
      leftTime,
      format: 'MM DD HH mm ss',
      formatDate: ({date, formatStr}) => {
        let duration = Number(date || 0);

        return CountdownTimeUnits.reduce((current, [name, unit]) => {
          if (current.indexOf(name) !== -1) {
            const value = Math.floor(duration / unit);
            duration -= value * unit;
            return current.replace(new RegExp(`(${name}+)(?![^<>]*>|[^>]*<\\/[a-z]+>)`, 'g'), (match: string) => {
              return '<span class="landing-countdown__item"><span class="landing-countdown__value">' +
                value.toString().padStart(match.length, '0') + '</span><span class="landing-countdown__label">' +
                this.getTranslatedMonthFromObj(name) +
                '</span></span>';
            });
          }

          return current;
        }, formatStr);
      },
    };
  }

  smoothScroll(id: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const target = document.querySelector(id);
    if (target instanceof HTMLElement) {
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  onMenuButtonClick(event: Event): void {
    event.preventDefault();
    event.stopImmediatePropagation();
    const menu = this.menuViewChild?.nativeElement;
    if (!menu) {
      return;
    }

    if (this.isMenuActive) {
      this.addClass(menu, 'fadeOutUp');
      this.isMenuActive = false;
      return;
    }

    this.addClass(menu, 'menu-active fadeInDown');
    this.isMenuActive = true;
  }

  closeMenu(): void {
    this.isMenuActive = false;
    const menu = this.menuViewChild?.nativeElement;
    if (menu) {
      this.removeClass(menu, 'menu-active fadeInDown fadeOutUp');
    }
  }

  onMenuAnimationEnd(): void {
    const menu = this.menuViewChild?.nativeElement;
    if (!menu) {
      return;
    }

    if (this.isMenuActive) {
      this.removeClass(menu, 'fadeInDown');
      return;
    }

    this.removeClass(menu, 'menu-active fadeOutUp');
  }

  addClass(element: HTMLElement, classNames: string): void {
    const classNamesArr = classNames.split(' ');
    for (const className of classNamesArr) {
      element.classList.add(className);
    }
  }

  removeClass(element: HTMLElement, classNames: string): void {
    const classNamesArr = classNames.split(' ');
    for (const className of classNamesArr) {
      element.classList.remove(className);
    }
  }

  public getTranslatedMonthFromObj(key: string): string {
    return this.text[key]?.toLowerCase() || key.toLowerCase();
  }

  public async handleTranslationChange(): Promise<void> {
    this.text = await this.translateTimer();
    this.loadFooterPages();
  }

  public resolvePageRoute(page: StaticPageApiModel): string {
    return this.staticPageRS.buildPublicRoute(page);
  }

  public handleSectionNavigation(event: Event, section: string): void {
    event.preventDefault();
    this.smoothScroll(section);
    this.closeMenu();
  }

  public handleFeatureNavigation(event: Event, item: LandingFeatureItem): void {
    if (item.section) {
      this.handleSectionNavigation(event, item.section);
      return;
    }

    this.closeMenu();
  }

  private async translateTimer(): Promise<Record<string, string>> {
    const template: Record<string, string> = {
      M: 'misc.months.short',
      D: 'misc.days.short',
      H: 'misc.hours.short',
      m: 'misc.minutes.short',
      s: 'misc.seconds.short',
    };

    return await this.translate.get(Object.values(template)).toPromise().then((res) => {
      const result: Record<string, string> = {};
      Object.entries(template).forEach(([key, value]) => {
        result[key] = res[value];
      });
      return result;
    });
  }

  private getTimestampIntervalDiffFromNow(targetDate: Date): number {
    const now = new Date();
    return (targetDate.getTime() - now.getTime()) / 1000;
  }

  private getVisibleSectionLinks(sectionLinks: LandingNavLink[]): LandingNavLink[] {
    return this.shouldShowExtendedLandingContent
      ? sectionLinks
      : sectionLinks.filter((sectionLink) => sectionLink.section === '#header');
  }

  private createForm(): void {
    this.form = this.fb.group({
      firstName: [''],
      lastName: [''],
      email: [
        '',
        [
          RxwebValidators.required(),
          RxwebValidators.minLength({value: 4}),
          RxwebValidators.maxLength({value: 255}),
          RxwebValidators.email()
        ]
      ],
    });
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (!this.form.valid) {
      return;
    }

    this.loading = true;

    this.newsletterContactRS.create(this.form.value as NewsletterContactApiModel)
      .subscribe({
        next: () => {
          this.form.reset();
          this.loading = false;
          this.rns.emitMessage(this.translate.instant('landing.newsletter_success_thanks'));
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.reh.handleFormErrors(this.form, errorResponse);
          this.reh.handleServerErrors(errorResponse);
          this.loading = false;
        }
      });
  }

  private loadFooterPages(): void {
    this.staticPageRS.getNavigation('landing_footer').subscribe({
      next: (pages) => {
        this.footerPages = pages;
      },
      error: () => {
        this.footerPages = [];
      }
    });
  }
}
